import React, { useEffect, useMemo, useState } from "react";
import { getTrendingPresetPickerCopy } from "../../config/dashboard/dashboardCategoryModalConfig";
import { getDemoSamplePool } from "../../config/dashboard/demoAssetsConfig";
import { useRandomDemoSamples } from "../../hooks/dashboard/useRandomDemoSamples";
import {
  getTrendingPresetCategoryBadge,
  getTrendingPresetSubLabel,
} from "./trendingPresetUtils";
import "./TrendingToolUploadModal.css";
import "../../components/ui/CategorySeeAllModal.css";
import { ModalUploadLegalNotice } from "./UploadLegalNotice";

function PresetIcon({ preset }) {
  if (preset?.icon_url) {
    return (
      <img
        src={preset.icon_url}
        alt=""
        className="v1-preset-select-icon"
        decoding="async"
        loading="lazy"
      />
    );
  }
  return (
    <span className="v1-preset-select-icon-fallback" aria-hidden="true">
      📁
    </span>
  );
}

const TrendingToolUploadModal = ({
  tool,
  isOpen,
  onClose,
  onUpload,
  presets = [],
  presetsLoading = false,
  isProcessing = false,
  /** When true, "Try these" loads pre-cutout CDN assets (no BG removal API). */
  samplesClickable = true,
  onSampleClick,
}) => {
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [listOpen, setListOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const hasPresetPicker = presets.length > 0 || presetsLoading;

  useEffect(() => {
    if (!isOpen) {
      setSelectedPreset(null);
      setListOpen(false);
      setSearchTerm("");
    }
  }, [isOpen, tool?.id]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        if (listOpen) {
          setListOpen(false);
        } else {
          onClose();
        }
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen, listOpen, onClose]);

  const filteredPresets = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return presets;

    return presets.filter((preset) => {
      const name = (preset.name || "").toLowerCase();
      const label = getTrendingPresetSubLabel(preset, preset.categoryDefaultLabel).toLowerCase();
      const category = (preset.categoryName || "").toLowerCase();
      return name.includes(term) || label.includes(term) || category.includes(term);
    });
  }, [presets, searchTerm]);

  const samplePool = getDemoSamplePool(tool?.samplePool);
  const samples = useRandomDemoSamples(samplePool, {
    enabled: isOpen && Boolean(tool?.samplePool),
    resetKey: tool?.id,
  });

  if (!isOpen || !tool) return null;

  const selectedCategoryDefault = selectedPreset?.categoryDefaultLabel || "";
  const pickerCopy = getTrendingPresetPickerCopy(tool.id);

  const handlePresetPick = (preset) => {
    setSelectedPreset(preset);
    setListOpen(false);
  };

  const handleUploadClick = () => {
    if (hasPresetPicker && !selectedPreset) return;
    onUpload(selectedPreset);
  };

  return (
    <div className="v1-see-all-modal-overlay" onClick={onClose}>
      <div
        className="v1-trending-upload-modal-card"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="v1-trending-upload-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        <div className="v1-trending-upload-modal-body">
          <h2 className="v1-trending-upload-modal-title">{tool.modalTitle}</h2>
          <p className="v1-trending-upload-modal-description">{tool.modalDescription}</p>

          <div className={`v1-trending-upload-card${hasPresetPicker ? " v1-category-see-all-card" : ""}`}>
            {hasPresetPicker && (
              <div className="v1-category-preset-block">
                <label className="v1-category-preset-label" htmlFor="trending-preset-select">
                  {pickerCopy.label}
                </label>

                <button
                  id="trending-preset-select"
                  type="button"
                  className={`v1-preset-select-trigger${listOpen ? " v1-preset-select-trigger--open" : ""}`}
                  onClick={() => setListOpen((open) => !open)}
                  aria-haspopup="listbox"
                  aria-expanded={listOpen}
                  disabled={presetsLoading || isProcessing}
                >
                  <span className="v1-preset-select-trigger-content">
                    {selectedPreset ? (
                      <>
                        <PresetIcon preset={selectedPreset} />
                        <span className="v1-preset-select-trigger-text">
                          <span className="v1-preset-select-trigger-name">
                            {selectedPreset.name}
                          </span>
                          <span className="v1-preset-select-trigger-label">
                            {getTrendingPresetSubLabel(selectedPreset, selectedCategoryDefault)}
                          </span>
                        </span>
                      </>
                    ) : (
                      <span className="v1-preset-select-placeholder">
                        {presetsLoading ? pickerCopy.loadingText : pickerCopy.placeholder}
                      </span>
                    )}
                  </span>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                {listOpen && (
                  <div className="v1-preset-select-panel" role="listbox">
                    <div className="v1-preset-select-search-wrap">
                      <input
                        type="text"
                        className="v1-preset-select-search"
                        placeholder={pickerCopy.searchPlaceholder}
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        autoComplete="off"
                        autoFocus
                      />
                      <span className="v1-preset-select-search-icon" aria-hidden="true">
                        🔍
                      </span>
                    </div>

                    <div className="v1-preset-select-list">
                      {presetsLoading ? (
                        <p className="v1-preset-select-empty">{pickerCopy.loadingText}</p>
                      ) : filteredPresets.length === 0 ? (
                        <p className="v1-preset-select-empty">
                          {searchTerm.trim()
                            ? pickerCopy.emptySearchText
                            : pickerCopy.emptyListText}
                        </p>
                      ) : (
                        filteredPresets.map((preset) => {
                          const isSelected = selectedPreset?.id === preset.id;
                          return (
                            <button
                              key={`${preset.categoryId}-${preset.id}`}
                              type="button"
                              role="option"
                              aria-selected={isSelected}
                              className={`v1-preset-select-option v1-preset-select-option--with-badge${isSelected ? " v1-preset-select-option--active" : ""}`}
                              onClick={() => handlePresetPick(preset)}
                            >
                              <PresetIcon preset={preset} />
                              <span className="v1-preset-select-option-text">
                                <span className="v1-preset-select-option-name">
                                  {preset.name}
                                </span>
                                <span className="v1-preset-select-option-label">
                                  {getTrendingPresetSubLabel(
                                    preset,
                                    preset.categoryDefaultLabel
                                  )}
                                </span>
                              </span>
                              <span className="v1-preset-select-option-category">
                                {getTrendingPresetCategoryBadge(preset.categoryName)}
                              </span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className={hasPresetPicker ? "v1-category-upload-actions" : undefined}>
              <button
                type="button"
                className="v1-trending-upload-btn"
                onClick={handleUploadClick}
                disabled={isProcessing || (hasPresetPicker && (!selectedPreset || presetsLoading))}
              >
                <span className="v1-trending-upload-btn-icon" aria-hidden="true">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M8 14l3-3 2 2 3-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M12 3v4M10 5h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </span>
                Upload Image
              </button>
              {hasPresetPicker && (
                <>
                  <p className="v1-trending-upload-hint v1-category-upload-hint">
                    or drag and drop images · Paste image or <kbd>Ctrl + V</kbd>
                  </p>
                  {tool.creditText && (
                    <p className="v1-trending-upload-credit v1-category-see-all-credit">
                      {tool.creditText}
                    </p>
                  )}
                </>
              )}
            </div>

            {!hasPresetPicker && (
              <>
                <p className="v1-trending-upload-hint">or drag and drop images</p>
                <p className="v1-trending-upload-paste">
                  Paste image or <kbd>Ctrl + V</kbd>
                </p>
              </>
            )}
          </div>

          {!hasPresetPicker && tool.creditText && (
            <p className="v1-trending-upload-credit">{tool.creditText}</p>
          )}

          {samples.length > 0 && (
            <div className="v1-trending-upload-samples">
              <p className="v1-trending-upload-samples-label">
                No image? Try one of these:
              </p>
              <div className="v1-trending-upload-samples-row">
                {samples.map((sample, index) => {
                  const thumbClassName =
                    "v1-trending-upload-sample-thumb" +
                    (samplesClickable ? "" : " v1-trending-upload-sample-thumb--preview");

                  if (samplesClickable && onSampleClick) {
                    return (
                      <button
                        key={`${sample.alt}-${index}`}
                        type="button"
                        className={thumbClassName}
                        onClick={() => onSampleClick(sample)}
                        disabled={isProcessing}
                        aria-label={sample.alt}
                      >
                        <img src={sample.src} alt={sample.alt} decoding="async" loading="lazy" />
                      </button>
                    );
                  }

                  return (
                    <div
                      key={`${sample.alt}-${index}`}
                      className={thumbClassName}
                      aria-hidden="true"
                    >
                      <img src={sample.src} alt="" decoding="async" loading="lazy" />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <ModalUploadLegalNotice />
        </div>
      </div>
    </div>
  );
};

export default TrendingToolUploadModal;
